import {
  useInfiniteQuery,
  useQueries,
  useSuspenseInfiniteQuery,
  useSuspenseQuery,
} from '@tanstack/react-query';
import {
  SourcingChannels,
  SourcingService,
  VonqChannelDetails,
} from 'api-clients/monolith';
import _range from 'lodash/range';
import { Suspense, useMemo } from 'react';
import { useIntl } from 'react-intl';

import EmptyChannelsIcon from '@/assets/icons/empty-channels.svg';
import { EmptyState } from '@/components/ui/empty-state';
import { Separator } from '@/components/ui/separator';
import { useJobPostingContext } from '@/contexts/job-posting-context';
import { queryKeys } from '@/query-keys';

import { Sections } from './sections';

import type { SortBy } from './sort';

type SectionKey = 'popular' | 'free' | 'paid';

// Paid is warmed separately, via paidChannelsInfiniteQueryOptions — its cache entry has a
// different shape ({ pages, pageParams }) than these two's plain SourcingChannels.
const NON_PAID_SECTION_KEYS: Array<Exclude<SectionKey, 'paid'>> = [
  'popular',
  'free',
];

function sectionChannels(
  data: SourcingChannels,
  section: SectionKey,
): SourcingChannels['popular_channels'] {
  if (section === 'popular') return data.popular_channels;
  if (section === 'free') return data.free_channels;
  return data.paid_channels;
}

interface SectionLoaderProps {
  section: SectionKey;
  sectionTitle: string;
  sectionDescription: string;
  searchQuery?: string;
  sortBy?: SortBy;
  initialSelectedOpeningIds: string[];
}

function SectionLoader({
  section,
  sectionTitle,
  sectionDescription,
  searchQuery,
  sortBy,
  initialSelectedOpeningIds,
}: SectionLoaderProps) {
  const { data } = useSuspenseQuery({
    queryKey: [
      ...queryKeys.jobPosting.channels[section],
      initialSelectedOpeningIds,
      searchQuery,
      sortBy,
    ],
    queryFn: () =>
      SourcingService.getInternalApiSourcingSourcingChannels(
        initialSelectedOpeningIds,
        sortBy,
        section,
        searchQuery,
      ),
    staleTime: Infinity,
  });

  const channels = sectionChannels(data, section);

  if (channels.count === 0) return null;

  const hasNoVonqChannels = channels.vonq_channels.length === 0;

  return (
    <>
      <Sections
        title={sectionTitle}
        description={sectionDescription}
        channels={channels}
        sectionKey={section}
        isIndeedIntegrationEnabled={data.indeed_integration_enabled}
        forceExpanded={!!searchQuery || hasNoVonqChannels}
      />
      {section !== 'paid' && <Separator />}
    </>
  );
}

// Minimum vonq_price amount for a channel, or null when it carries no price at all. Mirrors
// VonqProductHelper#list_price_sort_amount so the frontend re-sort agrees with the backend.
function listPriceSortAmount(channel: VonqChannelDetails): number | null {
  const amounts = channel.vonq_price
    .map(price => price.amount)
    .filter((amount): amount is number => typeof amount === 'number');

  return amounts.length === 0 ? null : Math.min(...amounts);
}

// Mirrors VonqRecommendedChannels#compare_list_price_sort's asc branch — the only direction
// SortBy offers ('list_price.asc' | undefined; there's no 'list_price.desc' to sort by).
// Channels with no price always sort last; ties (including two no-price channels) break by
// product_id.
function compareByListPriceAscending(
  left: VonqChannelDetails,
  right: VonqChannelDetails,
): number {
  const leftAmount = listPriceSortAmount(left);
  const rightAmount = listPriceSortAmount(right);

  if (leftAmount === null && rightAmount === null) {
    return left.product_id < right.product_id ? -1 : 1;
  }
  if (leftAmount === null) return 1;
  if (rightAmount === null) return -1;

  const priceCmp = leftAmount - rightAmount;
  if (priceCmp !== 0) return priceCmp;

  return left.product_id < right.product_id ? -1 : 1;
}

// Paid boards page in via "Load More", so pages are merged into one list rather than replaced.
// The same Vonq product can appear on more than one page (see VonqRecommendedChannels), so
// vonq_channels is deduplicated by product_id; direct_channels don't page and are identical
// across pages, so the first page's copy is used as-is.
export function mergePaidChannelPages(
  pages: SourcingChannels[],
  sortBy?: SortBy,
): SourcingChannels['paid_channels'] {
  const seenProductIds = new Set<string>();
  const dedupedVonqChannels = pages
    .flatMap(page => page.paid_channels.vonq_channels)
    .filter(channel => {
      if (seenProductIds.has(channel.product_id)) return false;
      seenProductIds.add(channel.product_id);
      return true;
    });
  const vonqChannels =
    sortBy === 'list_price.asc'
      ? [...dedupedVonqChannels].sort(compareByListPriceAscending)
      : dedupedVonqChannels;
  const directChannels = pages[0].paid_channels.direct_channels;

  return {
    direct_channels: directChannels,
    vonq_channels: vonqChannels,
    count: directChannels.length + vonqChannels.length,
    next_offset: pages[pages.length - 1].paid_channels.next_offset,
  };
}

interface PaidChannelsInfiniteQueryArgs {
  initialSelectedOpeningIds: string[];
  searchQuery?: string;
  sortBy?: SortBy;
}

// One definition, used by both ChannelsContent's warmup (plain useInfiniteQuery, below) and
// PaidSectionLoader (useSuspenseInfiniteQuery). Same queryKey + queryFn means both hooks
// resolve to the same cache entry: the warmup's fetch becomes PaidSectionLoader's cache hit
// (no second network call, no second suspend), and it's the one entry queryKeys.jobPosting
// .channels.paid actually invalidates — matching how popular/free already share one cache
// between their own warmup and SectionLoader.
function paidChannelsInfiniteQueryOptions({
  initialSelectedOpeningIds,
  searchQuery,
  sortBy,
}: PaidChannelsInfiniteQueryArgs) {
  return {
    queryKey: [
      ...queryKeys.jobPosting.channels.paid,
      initialSelectedOpeningIds,
      searchQuery,
      sortBy,
    ],
    queryFn: ({ pageParam }: { pageParam: number | undefined }) =>
      SourcingService.getInternalApiSourcingSourcingChannels(
        initialSelectedOpeningIds,
        sortBy,
        'paid',
        searchQuery,
        pageParam,
      ),
    initialPageParam: undefined as number | undefined,
    // hasNextPage becomes true exactly when this returns non-undefined, so the "Load More"
    // CTA and next_offset are the same signal: coerce a null next_offset (no more pages) to
    // undefined, which is the sentinel useInfiniteQuery treats as "no more pages".
    getNextPageParam: (lastPage: SourcingChannels) =>
      lastPage.paid_channels.next_offset ?? undefined,
    staleTime: Infinity,
  };
}

type PaidSectionLoaderProps = Omit<SectionLoaderProps, 'section'>;

function PaidSectionLoader({
  sectionTitle,
  sectionDescription,
  searchQuery,
  sortBy,
  initialSelectedOpeningIds,
}: PaidSectionLoaderProps) {
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useSuspenseInfiniteQuery(
      paidChannelsInfiniteQueryOptions({
        initialSelectedOpeningIds,
        searchQuery,
        sortBy,
      }),
    );

  const channels = mergePaidChannelPages(data.pages, sortBy);

  // An empty page doesn't mean there's nothing left: VonqRecommendedChannels can filter every
  // raw Vonq result out of a page (excluded channel, non-positive price, allow_orders: false)
  // while Vonq's own `next` link still points past it. Only hide the section once there's
  // truly no more to fetch — otherwise later valid pages become unreachable.
  if (channels.count === 0 && !hasNextPage) return null;

  const hasNoVonqChannels = channels.vonq_channels.length === 0;

  return (
    <Sections
      title={sectionTitle}
      description={sectionDescription}
      channels={channels}
      sectionKey="paid"
      isIndeedIntegrationEnabled={data.pages[0].indeed_integration_enabled}
      forceExpanded={!!searchQuery || hasNoVonqChannels}
      hasMore={hasNextPage}
      isLoadingMore={isFetchingNextPage}
      onLoadMore={() => fetchNextPage()}
    />
  );
}

interface ChannelsContentProps {
  searchQuery?: string;
  sortBy?: SortBy;
}

export function ChannelsContent({ searchQuery, sortBy }: ChannelsContentProps) {
  const intl = useIntl();
  const { initialSelectedOpeningIds } = useJobPostingContext();

  const sectionMeta = useMemo(
    () => [
      {
        key: 'popular' as SectionKey,
        title: intl.formatMessage({
          defaultMessage: 'Top Picks',
          description: 'Too picked channels for posting campaigns',
        }),
        description: intl.formatMessage({
          defaultMessage:
            'Boards are selected based on account performance, role type and location.',
          description:
            'Description for top picked channels for posting campaigns',
        }),
      },
      {
        key: 'free' as SectionKey,
        title: intl.formatMessage({
          defaultMessage: 'Free job boards',
          description: 'Free job boards for posting campaigns',
        }),
        description: intl.formatMessage({
          defaultMessage:
            "Boards that post at no cost — no budget, no billing. Includes organic boards and Fountain's own channels like Pool, etc.",
          description: 'Description for free job boards for posting campaigns',
        }),
      },
      {
        key: 'paid' as SectionKey,
        title: intl.formatMessage({
          defaultMessage: 'Paid boards',
          description: 'Paid job boards for posting campaigns',
        }),
        description: intl.formatMessage({
          defaultMessage: 'Pay-per-click, CPA, or fixed-duration boards.',
          description: 'Description for paid job boards for posting campaigns',
        }),
      },
    ],
    [intl],
  );

  // Fire popular + free in parallel without suspending. This populates the cache so each
  // SectionLoader's useSuspenseQuery gets a cache hit.
  const results = useQueries({
    queries: NON_PAID_SECTION_KEYS.map(section => ({
      queryKey: [
        ...queryKeys.jobPosting.channels[section],
        initialSelectedOpeningIds,
        searchQuery,
        sortBy,
      ],
      queryFn: () =>
        SourcingService.getInternalApiSourcingSourcingChannels(
          initialSelectedOpeningIds,
          sortBy,
          section,
          searchQuery,
        ),
      staleTime: Infinity,
    })),
  });

  // Paid still has to start here alongside popular/free — otherwise, if popular and free
  // resolve empty first, allEmpty would flash true while paid is still loading. Same options
  // as PaidSectionLoader's useSuspenseInfiniteQuery, so this warmup fetch becomes that hook's
  // cache hit instead of a second one.
  const paidResult = useInfiniteQuery(
    paidChannelsInfiniteQueryOptions({
      initialSelectedOpeningIds,
      searchQuery,
      sortBy,
    }),
  );

  const allLoaded = results.every(r => r.isSuccess) && paidResult.isSuccess;
  // An empty first page doesn't mean paid has nothing — see the matching comment in
  // PaidSectionLoader. Only count paid as empty here once next_offset also confirms there's no
  // further page to try; otherwise this would flash the all-empty state over reachable results.
  const firstPaidPage = paidResult.data?.pages[0].paid_channels;
  const paidEmpty =
    firstPaidPage?.count === 0 && firstPaidPage.next_offset == null;
  const allEmpty =
    allLoaded &&
    results.every(
      (r, i) => sectionChannels(r.data!, NON_PAID_SECTION_KEYS[i]).count === 0,
    ) &&
    paidEmpty;

  if (allEmpty) {
    const isSearching = !!searchQuery;
    return (
      <div className="border-fx-neutral-200 flex min-h-0 flex-1 items-center justify-center rounded-lg border bg-white p-4 shadow-sm">
        <EmptyState
          icon={EmptyChannelsIcon}
          header={
            isSearching
              ? intl.formatMessage({
                  defaultMessage: 'No channels found',
                  description:
                    'Empty state header when a search query returns no matching job board channels',
                })
              : intl.formatMessage({
                  defaultMessage: 'No channels available',
                  description:
                    'Empty state header when no publishable job board channels are configured for the account',
                })
          }
          description={
            isSearching
              ? intl.formatMessage({
                  defaultMessage:
                    'No channels match your search. Try a different keyword.',
                  description:
                    'Empty state description when a search query returns no matching job board channels',
                })
              : intl.formatMessage({
                  defaultMessage:
                    'There are no publishable channels configured for this account at this time. Contact your administrator to get channels set up.',
                  description:
                    'Empty state description when no publishable job board channels are configured for the account',
                })
          }
        />
      </div>
    );
  }

  return (
    <div className="border-fx-neutral-200 flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto rounded-lg border bg-white p-4 shadow-sm">
      {sectionMeta.map(section =>
        section.key === 'paid' ? (
          <Suspense key={section.key} fallback={<Sections.Skeleton />}>
            <PaidSectionLoader
              sectionTitle={section.title}
              sectionDescription={section.description}
              searchQuery={searchQuery}
              sortBy={sortBy}
              initialSelectedOpeningIds={initialSelectedOpeningIds}
            />
          </Suspense>
        ) : (
          <Suspense key={section.key} fallback={<Sections.Skeleton />}>
            <SectionLoader
              section={section.key}
              sectionTitle={section.title}
              sectionDescription={section.description}
              searchQuery={searchQuery}
              sortBy={sortBy}
              initialSelectedOpeningIds={initialSelectedOpeningIds}
            />
          </Suspense>
        ),
      )}
    </div>
  );
}

export function Channels({ searchQuery, sortBy }: ChannelsContentProps) {
  const { initialSelectedOpeningIds } = useJobPostingContext();

  if (initialSelectedOpeningIds.length === 0) {
    return null;
  }

  return (
    <Suspense fallback={<Channels.Skeleton />}>
      <ChannelsContent searchQuery={searchQuery} sortBy={sortBy} />
    </Suspense>
  );
}

Channels.Skeleton = function ChannelsSkeleton() {
  return (
    <div className="border-fx-neutral-200 flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto rounded-lg border bg-white p-4 shadow-sm">
      {_range(3).map(index => (
        <Sections.Skeleton key={index} />
      ))}
    </div>
  );
};
